import {
    Attribute,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

// Increasing integer for generating unique ids for checkbox components.
let nextUniqueId = 0;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CHECKBOX_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => InputCheckboxComponent),
    multi: true,
};

@Component({
    selector: 'ca-input-checkbox',
    templateUrl: './input-checkbox.component.html',
    styleUrls: ['./input-checkbox.component.scss'],
    providers: [CHECKBOX_CONTROL_VALUE_ACCESSOR],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.required]': 'required ? "" : null',
        '[id]': 'id',
        '[class.checkbox-checked]': 'checked',
        '[class.checkbox-disabled]': 'disabled',
        '[class.checkbox-label-before]': 'labelPosition == "before"',
    },
})
export class InputCheckboxComponent implements ControlValueAccessor {
    /** Returns the unique id for the visual hidden input. */
    get inputId(): string {
        return `${this.id || this._uniqueId}-input`;
    }

    /** Whether the checkbox is required. */
    @Input() get required(): boolean {
        return this._required;
    }
    set required(value: boolean) {
        this._required = this.coerceBooleanProperty(value);
    }

    /**
     * Whether the checkbox is checked.
     */
    @Input() get checked(): boolean {
        return this._checked;
    }
    set checked(value: boolean) {
        if (value !== this.checked) {
            this._checked = value;
            this._changeDetectorRef.markForCheck();
        }
    }

    /**
     * Whether the checkbox is disabled. This fully overrides the implementation provided by
     * mixinDisabled, but the mixin is still required because mixinTabIndex requires it.
     */
    @Input() get disabled(): boolean {
        return this._disabled;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    set disabled(value: any) {
        const newValue = this.coerceBooleanProperty(value);

        if (newValue !== this.disabled) {
            this._disabled = newValue;
            this._changeDetectorRef.markForCheck();
        }
    }
    /** Tabindex of the component. */
    tabIndex: number;

    @Input('aria-label') ariaLabel = '';

    /** Whether the label should appear after or before the checkbox. Defaults to 'after' */
    @Input() labelPosition: 'before' | 'after' = 'after';

    /** Name value will be applied to the input element if present */
    @Input() name: string | null = null;

    /** Event emitted when the checkbox's `checked` value changes. */
    @Output() readonly change: EventEmitter<boolean> = new EventEmitter<boolean>();

    /** The value attribute of the native input element */
    @Input() value: string;

    /** The native `<input type="checkbox">` element */
    @ViewChild('input', { static: true }) _inputElement: ElementRef<HTMLInputElement>;

    private _uniqueId = `checkbox-${++nextUniqueId}`;

    /** A unique id for the checkbox input. If none is supplied, it will be auto-generated. */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    @Input() id: string = this._uniqueId;

    private _required: boolean;
    private _checked = false;
    private _disabled = false;

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        @Attribute('tabindex') tabIndex: string,
    ) {
        this.tabIndex = parseInt(tabIndex, 10) || 0;
    }

    /**
     * Called when the checkbox is blurred. Needed to properly implement ControlValueAccessor.
     * @docs-private
     */
    _onTouched: () => void = () => {
        // do nothing
    };

    // Implemented as part of ControlValueAccessor.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    writeValue(value: any): void {
        this.checked = !!value;
    }

    // Implemented as part of ControlValueAccessor.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerOnChange(fn: (value: any) => void): void {
        this._controlValueAccessorChangeFn = fn;
    }

    // Implemented as part of ControlValueAccessor.
    registerOnTouched(fn: () => void): void {
        this._onTouched = fn;
    }

    // Implemented as part of ControlValueAccessor.
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    getAriaChecked(): 'true' | 'false' {
        return this.checked ? 'true' : 'false';
    }

    /** Toggles the `checked` state of the checkbox. */
    toggle(): void {
        this.checked = !this.checked;
    }

    /**
     * Event handler for checkbox input element.
     * Toggles checked state if element is not disabled.
     * Do not toggle on (change) event since IE doesn't fire change event when
     *   indeterminate checkbox is clicked.
     */
    onInputClick(event: Event): void {
        // We have to stop propagation for click events on the visual hidden input element.
        // By default, when a user clicks on a label element, a generated click event will be
        // dispatched on the associated input element. Since we are using a label element as our
        // root container, the click event on the `checkbox` will be executed twice.
        // The real click event will bubble up, and the generated click event also tries to bubble up.
        // This will lead to multiple click events.
        // Preventing bubbling for the second event will solve that issue.
        event.stopPropagation();

        // If resetIndeterminate is false, and the current state is indeterminate, do nothing on click
        if (!this.disabled) {
            this.toggle();

            // Emit our custom change event if the native input emitted one.
            // It is important to only emit it, if the native input triggered one, because
            // we don't want to trigger a change event, when the `checked` variable changes for example.
            this._emitChangeEvent();
        } else if (!this.disabled) {
            // Reset native input when clicked with noop. The native checkbox becomes checked after
            // click, reset it to be align with `checked` value of `mat-checkbox`.
            this._inputElement.nativeElement.checked = this.checked;
        }
    }

    onInteractionEvent(event: Event): void {
        // We always have to stop propagation on the change event.
        // Otherwise the change event, from the input element, will bubble up and
        // emit its event object to the `change` output.
        event.stopPropagation();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _controlValueAccessorChangeFn: (value: any) => void = () => {
        // do nothing
    };

    private _emitChangeEvent(): void {
        this._controlValueAccessorChangeFn(this.checked);
        this.change.emit(this.checked);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private coerceBooleanProperty(value: any): boolean {
        return value != null && `${value}` !== 'false';
    }
}
